/**
 * Import foods button with file picker.
 *
 * Handles JSON file selection, validation, and import mutation.
 */

import * as stylex from "@stylexjs/stylex";
import { useMemo, useRef, useState } from "react";

import type {
  ImportFoodsResponse,
  SeedFood,
} from "@animus/frontend-features/nutrition/foods";
import {
  useApiConfig,
  useAuthStrategy,
} from "@animus/frontend-features/config";
import { createFoodsApi } from "@animus/frontend-features/nutrition/foods";
import { alert, button, spacing } from "@animus/primaris";

interface ImportFoodsButtonProps {
  onSuccess?: (result: ImportFoodsResponse) => void;
}

interface ParsedFoodsFile {
  foods: SeedFood[];
}

function isValidFoodsFile(data: unknown): data is ParsedFoodsFile {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  if (!("foods" in data && Array.isArray((data as ParsedFoodsFile).foods))) {
    return false;
  }
  return true;
}

function getErrorMessage(err: unknown): string {
  if (err instanceof SyntaxError) {
    return "Invalid JSON file";
  }
  if (err instanceof Error) {
    return err.message;
  }
  return "Failed to import foods";
}

function ImportFoodsButton({ onSuccess }: ImportFoodsButtonProps) {
  const { baseUrl } = useApiConfig();
  const auth = useAuthStrategy();
  const api = useMemo(() => createFoodsApi(baseUrl, auth), [baseUrl, auth]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  function handleClick() {
    fileInputRef.current?.click();
  }

  function resetFileInput() {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setError(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text) as unknown;

      if (!isValidFoodsFile(data)) {
        setError('Invalid file format. Expected { "foods": [...] }');
        resetFileInput();
        return;
      }

      setIsPending(true);
      const result = await api.importFoods({ foods: data.foods });
      setIsPending(false);

      if (result.isErr) {
        setError(result.error.message ?? "Failed to import foods");
        resetFileInput();
        return;
      }

      onSuccess?.(result.value);
    } catch (err) {
      setIsPending(false);
      setError(getErrorMessage(err));
    }

    resetFileInput();
  }

  return (
    <div>
      <input
        accept=".json"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: "none" }}
        type="file"
      />
      <button
        {...stylex.props(button.base, button.primary, button.small)}
        disabled={isPending}
        onClick={handleClick}
        type="button"
      >
        {isPending ? "Importing..." : "Import JSON"}
      </button>
      {error && (
        <div {...stylex.props(alert.error, spacing.mt2, spacing.px3)}>
          {error}
        </div>
      )}
    </div>
  );
}

export { ImportFoodsButton, type ImportFoodsButtonProps };
